import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { THEMES, deriveCSSVariables, DEFAULT_THEME, loadPresetsFromEngine, generateThemeFromSeed, randomTheme as randomThemeFromEngine, type ThemeDefinition } from '../lib/themeMap'

interface ThemeContextValue {
    themeName: string
    theme: ThemeDefinition
    setTheme: (name: string) => void
    setThemeFromSeed: (seedColor: string, isDark?: boolean) => void
    setRandomTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'en-theme'

/**
 * ThemeProvider sets CSS variables on a wrapper <div> that covers the entire app.
 * This avoids setting variables on document.documentElement (:root), which mobile
 * browsers don't reliably propagate to fixed/composited layers.
 * Every child — including fixed overlays, hidden tabs, bottom sheets — inherits
 * directly from this div.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
    const [themeName, setThemeName] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME } catch { return DEFAULT_THEME }
    })
    const [customTheme, setCustomTheme] = useState<ThemeDefinition | null>(null)

    const [, forceUpdate] = useState(0)
    useEffect(() => {
        loadPresetsFromEngine().then(() => {
            // Two migrations once KMP presets are loaded:
            //
            //  1. Bootstrap fallbacks (`dark` / `light`) render the Carbon
            //     default palette but sit outside THEME_GROUPS — migrate them
            //     to the canonical `carbon-dark` / `carbon-light` keys so the
            //     picker correctly shows "carbon" as selected.
            //  2. Invalidate stale theme names that no longer exist.
            setThemeName(prev => {
                if (prev === 'dark' && THEMES['carbon-dark']) {
                    try { localStorage.setItem(STORAGE_KEY, 'carbon-dark') } catch {}
                    return 'carbon-dark'
                }
                if (prev === 'light' && THEMES['carbon-light']) {
                    try { localStorage.setItem(STORAGE_KEY, 'carbon-light') } catch {}
                    return 'carbon-light'
                }
                if (THEMES[prev]) return prev
                try { localStorage.removeItem(STORAGE_KEY) } catch {}
                return DEFAULT_THEME
            })
            forceUpdate(n => n + 1)
        })
    }, [])

    const theme = customTheme ?? THEMES[themeName] ?? THEMES[DEFAULT_THEME]
    const vars = deriveCSSVariables(theme)

    // Also set on documentElement for things outside React (e.g. body background)
    const prevVarsRef = useRef<Record<string, string>>({})
    useEffect(() => {
        const root = document.documentElement
        // Remove old vars that are no longer present
        for (const key of Object.keys(prevVarsRef.current)) {
            if (!(key in vars)) root.style.removeProperty(key)
        }
        // Set current vars
        for (const [key, value] of Object.entries(vars)) {
            root.style.setProperty(key, value)
        }
        prevVarsRef.current = vars
    }, [vars])

    const setTheme = useCallback((name: string) => {
        if (THEMES[name]) {
            setThemeName(name)
            setCustomTheme(null)
            try { localStorage.setItem(STORAGE_KEY, name) } catch {}
        }
    }, [])

    const setThemeFromSeed = useCallback((seedColor: string, isDark?: boolean) => {
        generateThemeFromSeed(seedColor, isDark ?? theme.isDark ?? true).then(t => {
            if (t) {
                setCustomTheme(t)
            } else {
                console.warn('[Theme] generateThemeFromSeed returned null for', seedColor)
            }
        }).catch(e => console.error('[Theme] generateThemeFromSeed error:', e))
    }, [theme])

    const setRandomTheme = useCallback(() => {
        randomThemeFromEngine(theme.isDark ?? true).then(t => {
            if (t) {
                setCustomTheme(t)
            } else {
                console.warn('[Theme] randomTheme returned null')
            }
        }).catch(e => console.error('[Theme] randomTheme error:', e))
    }, [theme])

    return (
        <ThemeContext.Provider value={{ themeName: customTheme?.name ?? themeName, theme, setTheme, setThemeFromSeed, setRandomTheme }}>
            <div style={{ ...vars, width: '100%', height: '100%' } as React.CSSProperties}>
                {children}
            </div>
        </ThemeContext.Provider>
    )
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext)
    if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
    return ctx
}
